interface ScheduledAsyncOptions {
  wait: number
  maxWait: number
}

interface ScheduledAsync {
  schedule: () => void
  flush: () => Promise<void>
  cancel: () => void
}

export function createScheduledAsync(
  fn: () => Promise<void>,
  options: ScheduledAsyncOptions,
): ScheduledAsync {
  const { wait, maxWait } = options

  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let firstTriggerTime: number | null = null
  let runningPromise: Promise<void> | null = null
  let hasPendingSchedule = false

  function clearTimer() {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  async function run() {
    if (runningPromise) {
      hasPendingSchedule = true
      return
    }

    firstTriggerTime = null
    runningPromise = fn()

    try {
      await runningPromise
    } finally {
      runningPromise = null
      if (hasPendingSchedule) {
        hasPendingSchedule = false
        schedule()
      }
    }
  }

  function schedule() {
    if (runningPromise) {
      hasPendingSchedule = true
      return
    }

    const now = Date.now()

    if (firstTriggerTime === null) {
      firstTriggerTime = now
    }

    const elapsed = now - firstTriggerTime
    const remaining = maxWait - elapsed

    clearTimer()

    if (remaining <= 0) {
      run()
    } else {
      timeoutId = setTimeout(run, Math.min(wait, remaining))
    }
  }

  function cancel() {
    clearTimer()
    firstTriggerTime = null
    hasPendingSchedule = false
  }

  async function flush() {
    cancel()

    if (runningPromise) {
      await runningPromise
      hasPendingSchedule = false
    }

    runningPromise = fn()
    try {
      await runningPromise
    } finally {
      runningPromise = null
    }
  }

  return { schedule, flush, cancel }
}
